// Copyright (c) Docugami, Inc. All rights reserved.

import { IconButton, TooltipHost, OverflowSet } from "@fluentui/react";
import { ICommandButton } from "components/cards/CommandButton";
import { EntryColumnConfirmationButton } from "components/common/ReportBindingConfirmationButtons";
import { appContentColor, primaryTheme } from "components/shell/presentation/AppStyles";
import { dgIconNames } from "hooks/dgIcons";
import { ReportEntry } from "networking/reportsApi";
import { ReactNode, useMemo, useState } from "react";
import { CellStyles, CellContentContainer, ReportHeaderText, TitleCellContainer, CenterColumnContainer, ControlsColumnContainer } from "./ReportGridCellStyles";
import { getClassNames } from "components/cards/CardStyles";
import styled from "styled-components";
import { tagFont } from "components/common/Fonts";
import { AutomationProps } from "utils/Automation";
import { AddZeroShotEntryCallout } from "./AddItemButton";
import { IReportGroup } from "store/reports/selectors";
import { useLabelQualityControlCommand } from "hooks/LabelQualityControl";
import { useAppSelector } from "hooks/redux";

const EntryHeadingText = styled(ReportHeaderText).attrs({
    'data-componentname': 'EntryHeadingText'
})`
    font-family: ${tagFont};
    display: block;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    flex: 1;
    margin: 0px 0px 0px 15px;
`;

const OverflowSetContainer = styled.div`
    display: inline-block;
    width: 24px;
    height: 26px;
    vertical-align: middle;
`;

interface TitleCellProps extends AutomationProps {
    id: string;
    title: string;
    showConfirmation: boolean;
    overflowCommands?: ICommandButton[];
    className?: string;
    titleIcon?: ReactNode;
    isGrouped?: boolean;
    styles?: CellStyles;
    overlays?: ReactNode[];
    entry: ReportEntry;
}

const TitleCell = (props: TitleCellProps): JSX.Element | null => {
    const [showHiddenMenuItems, setShowHiddenMenuItems] = useState(false);

    const overflowItems = useMemo(() => {
        return props.overflowCommands?.map(c => ({
            key: c.label,
            name: c.label,
            trigger: c.onClick,
            onClick: c.onClick,
            isVisible: c.canExecute,
            disabled: !c.canExecute,
            iconDescriptor: c.icon
        }))
    }, [props.overflowCommands]);

    const onRenderOverflowButton = (overflowItems: any[] | undefined): JSX.Element => (
        <IconButton
            iconProps={{ iconName: "MoreVertical" }}
            styles={{
                root: {
                    "i": { fontWeight: 700 },
                    width: "24px",
                    padding: "0px 1px",
                },
                icon: {
                    fontWeight: 700,
                    color: appContentColor
                },
            }}
            menuProps={{ items: overflowItems!, styles: { root: { backgroundColor: primaryTheme.palette.white } } }}
            onRenderMenuIcon={() => null}
        />
    );

    return (
        <CellContentContainer
            id={props.id}
            onMouseOver={() => setShowHiddenMenuItems(true)}
            onMouseLeave={() => setShowHiddenMenuItems(false)}
            data-automation-id={props.automationId}
            className={getClassNames(props.styles?.root)}
            showBottomBorder
        >
            <TitleCellContainer>
                <CenterColumnContainer>
                    <TooltipHost
                        content={props.title}
                    >
                        <EntryHeadingText>
                            {props.title}
                        </EntryHeadingText>
                    </TooltipHost>
                </CenterColumnContainer>

                <ControlsColumnContainer>
                    <OverflowSetContainer>
                        {showHiddenMenuItems && (
                            <OverflowSet
                                styles={{
                                    root: { height: 26, width: '24px' },
                                    overflowButton: {
                                        height: 26,
                                        width: '24px',
                                        'button': { height: 26, width: '24px' }
                                    }
                                }}
                                overflowItems={overflowItems}
                                onRenderItem={() => null}
                                onRenderOverflowButton={onRenderOverflowButton}
                            />
                        )}
                    </OverflowSetContainer>
                    <EntryColumnConfirmationButton entry={props.entry} />
                </ControlsColumnContainer>
            </TitleCellContainer>
            {props.overlays}
        </CellContentContainer>
    );
}

interface EntryTitleCellProps extends AutomationProps {
    entry: ReportEntry;
    entryIndex: number;
    isLocked?: boolean;
    isGrouped?: boolean;
    styles?: CellStyles;
    showEntryConfirmation: boolean;
    onRemoveEntry: (entry: ReportEntry) => void;
    canRemoveEntry: boolean;
    columnGroups: IReportGroup[];
    overlays?: ReactNode[];
}

export const EntryTitleCell = (props: EntryTitleCellProps): JSX.Element | null => {
    const docsetId = useAppSelector(state => state.reportsSlice.clusterId);
    const [showTextField, setShowTextField] = useState(false);

    const [targetIndex, setTargetIndex] = useState(props.entryIndex);
    const groupId = useMemo(() =>
        props.columnGroups.find(group => targetIndex > group.startIndex && targetIndex < (group.startIndex + group.count))?.data?.id,
        [props.columnGroups, targetIndex])

    const qualityControlSettings = useLabelQualityControlCommand(docsetId, props.entry.labelId);

    const cellId = `entryTitleCell_${props.entry.id}`;

    const onAddItemClicked = (targetIndex: number) => () => {
        setShowTextField(true);
        setTargetIndex(targetIndex);
    };

    return (
        <>
            <TitleCell
                id={cellId}
                automationId={props.automationId}
                title={props.entry.title}
                showConfirmation={props.showEntryConfirmation}
                entry={props.entry}
                overflowCommands={[{
                    label: "Remove this item",
                    canExecute: props.canRemoveEntry,
                    onClick: () => props.onRemoveEntry(props.entry),
                    icon: {
                        name: dgIconNames[dgIconNames.DgTrashCan],
                        width: 26,
                        height: 30,
                        defaultColor: appContentColor
                    }
                },
                {
                    label: "Add column to left",
                    canExecute: !props.isLocked,
                    onClick: onAddItemClicked(props.entryIndex),
                    icon: {
                        name: 'Add',
                        width: 26,
                        height: 30,
                        defaultColor: appContentColor
                    }
                },
                {
                    label: "Add column to right",
                    canExecute: !props.isLocked,
                    onClick: onAddItemClicked(props.entryIndex + 1),
                    icon: {
                        name: 'Add',
                        width: 26,
                        height: 30,
                        defaultColor: appContentColor
                    }
                },
                {
                    label: qualityControlSettings.label,
                    canExecute: true,
                    onClick: qualityControlSettings.execute,
                    icon: {
                        name: dgIconNames[dgIconNames.DgBang],
                        width: 26,
                        height: 30,
                        defaultColor: appContentColor
                    }
                }]}
                isGrouped={props.isGrouped}
                styles={props.styles}
                overlays={qualityControlSettings.isActive ? [qualityControlSettings.visual] : props.overlays}
            />
            <AddZeroShotEntryCallout
                reportId={props.entry.reportId}
                entryIndex={targetIndex}
                targetElementId={cellId}
                hidden={!showTextField}
                onDismiss={() => setShowTextField(false)}
                groupId={groupId}
            />
        </>
    )
}
